import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { CartCalculationOrderResponse } from '~/api/Catalog';
import { useDateLang } from '~/hooks/useDateLang';
import { useGlobal } from '~/hooks/useGlobal';
import { useTimeFormat } from '~/hooks/useTimeFormat';
import { mainStore, orderStore } from '~/stores';
import DeliveryType from '~/stores/CheckoutStore/Delivery';
import { formatPriceWithCurrency } from '~/utils/formaters';

import { getSelectedDaySplited } from '../../utils';

import DeliveryInfo from '../DeliveryInfo/DeliveryInfo';

import styles from './DeliveryPanel.module.scss';

interface DeliveryPanelProps {
  order: CartCalculationOrderResponse;
  delivery: DeliveryType;
  expressDeliveryEnabled?: boolean | undefined;
  showPrice?: boolean;
  type: 'express' | 'slot';
}

const DeliveryPanel: FC<DeliveryPanelProps> = ({
  order,
  delivery,
  showPrice = false,
  type,
}) => {
  const { t } = useTranslation();
  const { locale } = useDateLang();
  const { timeFormat } = useTimeFormat();
  const { isMobile } = useGlobal();
  const selectedDay =
    delivery.slot &&
    getSelectedDaySplited(delivery.slot, timeFormat, locale).day;

  const selectedTime =
    delivery.slot &&
    getSelectedDaySplited(delivery.slot, timeFormat, locale).time;

  const deliveryPrice = formatPriceWithCurrency(
    mainStore.convertPenceToPounds(order.delivery_info.paid_price),
  );
  const isDeliveryFree = order.is_delivery_free;

  const deliveryTitle =
    type === 'express'
      ? t('checkout:delivery:expressDeliveryTitle', {
          time: orderStore.etaCalculation?.duration.range,
        })
      : delivery.slotDeliveryDetails?.isAutomatic
        ? t('checkout:pleaseSelectDeliveryTime')
        : selectedDay;
  const deliveryTitleExtra =
    type === 'express'
      ? t('checkout:delivery:deliveryInMin', {
          time: orderStore.etaCalculation?.duration.range,
        })
      : delivery.slotDeliveryDetails?.isAutomatic
        ? null
        : selectedTime;

  const price = isDeliveryFree ? (
    <p className={styles.deliveryFree}>{t('deliveryFree')}</p>
  ) : (
    <p className={styles.contentSecondary}>
      {t('deliveryFee')}:&nbsp;
      {deliveryPrice}
    </p>
  );

  return (
    <div className={styles.delivery}>
      <div className={styles.contentPrimary}>
        <div className={styles.contentPrimaryBlock}>
          <p
            className={classes([
              styles.contentPrimaryText,
              type === 'slot' &&
                delivery.slotDeliveryDetails?.isAutomatic &&
                isMobile &&
                styles.contentPrimaryTextColor,
            ])}
          >
            {deliveryTitle}
          </p>
          {!deliveryTitleExtra && (
            <DeliveryInfo classNames={styles.contentPrimaryIcon} />
          )}
        </div>
        {deliveryTitleExtra ? (
          <div className={styles.contentPrimaryBlock}>
            <p className={styles.contentPrimaryText}>{deliveryTitleExtra}</p>
            <DeliveryInfo classNames={styles.contentPrimaryIcon} />
          </div>
        ) : null}
      </div>
      {showPrice && !delivery.slotDeliveryDetails?.isAutomatic && price}
    </div>
  );
};

export default observer(DeliveryPanel);
